/**
 * Historique des conversations (contrat par étapes, docs/refonte-assistant/CONCEPTION-historique.md de
 * l'Api-Ai) : ce que l'app garde d'un tour, et sa relecture sans confiance quand on rouvre une
 * conversation.
 *
 * Un tour gardé, c'est son affichage (la réponse telle que l'app l'a montrée) et ses lectures (les
 * mails et événements lus, par ref), sans le corps des mails ni leurs pièces jointes ; l'échange, que
 * l'assistant relit, voyage à côté.
 */

import type { AgendaItem } from "../agenda";
import type { ViewFilter } from "../customView";
import type { Mail } from "../mail";
import { isRecord } from "./etapes";
import type {
    ComponentData,
    ConversationContextSize,
    SearchMailsParams,
} from "./types/conversation";

/** Un mail lu pendant le tour, avec sa ref et le filtre de sa lecture (pour agir dessus plus tard). */
export type HistoryMailRead = {
    ref: string;
    mail: Mail;
    filter: ViewFilter;
    criteria: SearchMailsParams;
};

export type HistoryEventRead = { ref: string; item: AgendaItem };

/** Ce que l'app garde d'un tour, dans `payload` (opaque et chiffré côté Api-Backend). */
export type HistoryTurnPayload = {
    affichage: ComponentData;
    lectures: { mails: HistoryMailRead[]; evenements: HistoryEventRead[] };
    /** La place occupée à la fin du tour (la jauge, dès la réouverture). */
    contexte?: ConversationContextSize;
};

/** Ce que l'app garde d'un compactage. */
export type HistoryCompactionPayload = {
    resume: string;
    contexte?: ConversationContextSize;
};

/** Un mail, au sens du SDK : on n'en garde que ce qui s'affiche dans le chat. */
const looksLikeMail = (value: Record<string, unknown>) =>
    "internetMessageId" in value && "uniqueBody" in value;

/**
 * Copie gardable d'une valeur : sans fonction (rappels de pagination), et chaque mail sans son corps,
 * ses pièces jointes ni ses liens — il se rouvre par son identifiant.
 */
export function toHistoryValue<T>(value: T): T {
    return JSON.parse(
        JSON.stringify(value, (_key, inner: unknown) =>
            isRecord(inner) && looksLikeMail(inner)
                ? {
                      ...inner,
                      message: "",
                      uniqueBody: "",
                      attachments: [],
                      links: [],
                  }
                : inner,
        ),
    ) as T;
}

/** Le `payload` d'un tour gardé, lu sans confiance : illisible, `undefined` (le tour est sauté). */
export function readHistoryTurn(
    payload: unknown,
): HistoryTurnPayload | undefined {
    if (!isRecord(payload) || !isRecord(payload.affichage)) return undefined;
    const lectures = isRecord(payload.lectures) ? payload.lectures : {};
    const mails = Array.isArray(lectures.mails)
        ? lectures.mails.filter(
              (read): read is HistoryMailRead =>
                  isRecord(read) &&
                  typeof read.ref === "string" &&
                  isRecord(read.mail),
          )
        : [];
    const evenements = Array.isArray(lectures.evenements)
        ? lectures.evenements.filter(
              (read): read is HistoryEventRead =>
                  isRecord(read) &&
                  typeof read.ref === "string" &&
                  isRecord(read.item),
          )
        : [];
    return {
        affichage: payload.affichage as ComponentData,
        lectures: { mails, evenements },
    };
}

const isContextSize = (value: unknown): value is ConversationContextSize =>
    isRecord(value) &&
    typeof value.used === "number" &&
    typeof value.max === "number" &&
    value.max > 0;

/** La place gardée avec un tour ou un compactage ; illisible ou absente : `undefined`. */
export function readHistoryContext(
    payload: unknown,
): ConversationContextSize | undefined {
    return isRecord(payload) && isContextSize(payload.contexte)
        ? payload.contexte
        : undefined;
}

/** Le résumé d'un compactage gardé ; illisible : « ». */
export function readHistoryCompaction(payload: unknown): string {
    return isRecord(payload) && typeof payload.resume === "string"
        ? payload.resume
        : "";
}
